/**
 * B1084 (M24 leg 2) slice (b) — the cart steps as WebMCP tools.
 *
 * Slice (a) (./webMcp.ts) lets an agent list, open and read showtimes. These
 * carry it the rest of the way the buyer walks: pick a showtime, set the
 * party, add it (the tickets step's Continue), apply a code, fill the buyer
 * fields, and read the cart. Each tool is the page's own door — the same
 * setter or press a click reaches — and each refusal is the page's own
 * sentence plus the call that fixes it.
 *
 * Two things stay the buyer's and no tool here reaches them:
 *  - the acknowledgement tick (the show's policies and its age line, B615) —
 *    review_checkout names it; nothing ticks it;
 *  - the payment: the card (or Stripe Link) goes in Stripe's own form and the
 *    buyer presses Pay. There is no payment tool, so no new payment path.
 *
 * A group-sized party is the groups desk's (B549), never checkout's, and a
 * journey (round trip) keeps its sailings on the page. Pure: no React, no I/O.
 */
import { compositionIssues, issueHint, maxQuantity, quantityOf, totalQuantity, type Composition } from "./composition.js";
import { groupParty } from "./groupBooking.js";
import { BUYER_EMAIL_MISSING, BUYER_NAME_MISSING, buyerFieldProblems } from "./buyerAttach.js";
import { majorUnits } from "./jsonLd.js";
import { needsDepartureStep } from "./browse.js";
import type { Money } from "./money.js";
import type { ProductSummary } from "../ui/screens.js";
import type { WebMcpResult, WebMcpTool } from "./webMcp.js";
import { AGENT_NAME_MAX } from "@transact/contracts";

/** The open offering's tickets step, as the page holds it right now. */
export interface TicketsView {
  readonly card: ProductSummary;
  /** A journey (B57) walks trip type and sailings on the page. */
  readonly journey: boolean;
  /** The departures the step draws; undefined while they load. `left` absent = not read. */
  readonly departures?: readonly { readonly slotId: string; readonly start: number; readonly left?: number | undefined }[] | undefined;
  readonly chosenSlotId?: string | undefined;
  readonly comp: Composition;
  readonly groupThreshold?: number | undefined;
}

/** The cart & pay step, as the page holds it right now. */
export interface CheckoutView {
  readonly lines: readonly {
    readonly productName: string;
    readonly ticketType?: string | undefined;
    readonly quantity: number;
    readonly start?: number | undefined;
    readonly amount?: Money | undefined;
  }[];
  readonly adjustments: readonly { readonly label: string; readonly amount: Money }[];
  readonly charges: readonly { readonly label: string; readonly amount: Money }[];
  readonly total?: Money | undefined;
  readonly buyer: { readonly name: string; readonly email: string; readonly phone: string };
  /** The Pay press asks for a name here (B415 b). */
  readonly nameRequired: boolean;
  /** …and an email in the dialog walk (B545 d). */
  readonly emailRequired: boolean;
  /** The one policy line to tick (B615); undefined = nothing to tick. */
  readonly acknowledgement?: string | undefined;
  readonly acknowledged: boolean;
  /** An add, a code or a line change is still on the wire. */
  readonly busy: boolean;
  /** The page's own refusal sentence on screen, if any. */
  readonly notice?: string | undefined;
}

export type CodeOutcome = { readonly applied: true; readonly total?: Money | undefined } | { readonly refused: string };

export interface CartToolDeps {
  /** undefined = no offering is open. */
  tickets(): TicketsView | undefined;
  /** The departure pick a tap on a showtime makes. */
  chooseShowtime(slotId: string): void;
  /** The stepper's own setter (the page bounds it as the + button does). */
  setQuantity(variantId: string, quantity: number): void;
  /** The tickets step's Continue press. */
  addToCart(): void;
  /** The cart's Apply press; its answer, in the page's words. undefined = not sent: the cart was busy (B338's guard). */
  applyCode(code: string): Promise<CodeOutcome | undefined>;
  /** The typed buyer fields. */
  setBuyer(fields: { readonly name?: string; readonly email?: string; readonly phone?: string }): void;
  checkout(): CheckoutView;
  /** B1084 (d) — an add made through the tools makes the cart the agent's (channel = agent at checkout). */
  markAgent?(name: string): void;
  /**
   * B1084 (c) — the continue_url: cart.handoff's single-use, 15-minute link
   * that opens THIS cart in a person's own browser (UCP's shape). undefined =
   * not minted (no cart, or the door refused) — the rest of the answer stands.
   */
  handOff?(): Promise<{ readonly continueUrl: string } | undefined>;
}

/** B1084 (c) — what review_checkout says beside the link. */
export const CONTINUE_NOTE =
  "To let a person finish on their own device, give them continueUrl — it opens this cart there, works once and expires in 15 minutes. This page then no longer holds the cart.";

/** B1084 (d) — the order's agent when the agent declared no name on add_to_cart. */
export const AGENT_UNNAMED = "Unnamed agent";

/** The steps after slice (a)'s, in the order a buyer takes them. */
export const CART_TOOL_NAMES = ["choose_showtime", "set_tickets", "add_to_cart", "apply_code", "set_buyer_details", "review_checkout"] as const;

const said = (text: string): WebMcpResult => ({ content: [{ type: "text", text }] });
const refused = (text: string): WebMcpResult => ({ content: [{ type: "text", text }], isError: true });
const json = (value: unknown): WebMcpResult => said(JSON.stringify(value));
const price = (m: Money): { price: string; currency: string } => ({ price: majorUnits(m.amount), currency: m.currency });

const field = (input: unknown, key: string): unknown =>
  typeof input === "object" && input !== null ? (input as Record<string, unknown>)[key] : undefined;
const text = (input: unknown, key: string): string | undefined => {
  const v = field(input, key);
  return typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
};

/** "General Admission and Child" / "A, B and C". */
function andList(words: readonly string[]): string {
  return words.length <= 1 ? (words[0] ?? "") : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

function partyLine(card: ProductSummary, comp: Composition): string {
  const parts = card.variants.filter((v) => quantityOf(comp, v.variantId) > 0).map((v) => `${quantityOf(comp, v.variantId)} × ${v.name}`);
  return parts.length === 0 ? "no tickets" : parts.join(", ");
}

function partyTotal(card: ProductSummary, comp: Composition): Money | undefined {
  let sum: Money | undefined;
  for (const v of card.variants) {
    const q = quantityOf(comp, v.variantId);
    if (q === 0) continue;
    if (v.displayPrice === undefined) return undefined;
    sum = { amount: (sum?.amount ?? 0) + v.displayPrice.amount * q, currency: v.displayPrice.currency };
  }
  return sum;
}

const groupSentence = (size: number): string =>
  `A party this size is a group of ${size} here — the business books groups by inquiry on the page, not through checkout. Keep the party under ${size} to buy online.`;

export function cartTools(deps: CartToolDeps): readonly WebMcpTool[] {
  /** The open offering, if it is the one the call names. */
  const opened = (input: unknown): { view: TicketsView } | { refusal: WebMcpResult } => {
    const id = text(input, "productId");
    const view = deps.tickets();
    if (view === undefined) return { refusal: refused(`No offering is open — call open_offering${id !== undefined ? ` with “${id}”` : ""} first.`) };
    if (id === undefined) return { refusal: refused(`Say which offering with a productId — ${view.card.name} (“${view.card.productId}”) is open.`) };
    if (id !== view.card.productId) return { refusal: refused(`${view.card.name} is the open offering — call open_offering with “${id}” first.`) };
    if (view.journey) return { refusal: refused(`${view.card.name} is a trip: its trip type and legs are picked on the page itself, not through these tools.`) };
    return { view };
  };

  const PRODUCT = { productId: { type: "string", description: "The open offering's id (from list_offerings)." } } as const;

  return [
    {
      name: "choose_showtime",
      description: "Pick one time of the open offering — the same pick as tapping it on the page. Use a slotId from list_showtimes.",
      inputSchema: {
        type: "object",
        properties: { ...PRODUCT, slotId: { type: "string", description: "A slotId from list_showtimes." } },
        required: ["productId", "slotId"],
        additionalProperties: false,
      },
      execute: async (input) => {
        const found = opened(input);
        if ("refusal" in found) return found.refusal;
        const { card, departures } = found.view;
        if (!needsDepartureStep(card)) return refused(`${card.name} has no times to pick — call set_tickets.`);
        const slotId = text(input, "slotId");
        if (slotId === undefined) return refused(`Say which time with a slotId — call list_showtimes with “${card.productId}”.`);
        if (departures === undefined) return refused(`${card.name}'s times are still loading — ask again in a moment.`);
        const slot = departures.find((d) => d.slotId === slotId);
        if (slot === undefined) return refused(`${card.name} has no time “${slotId}” — call list_showtimes with “${card.productId}” for the ones on sale.`);
        if (slot.left !== undefined && slot.left <= 0) return refused(`That time is sold out — call list_showtimes with “${card.productId}” and pick another.`);
        deps.chooseShowtime(slotId);
        return said(`Picked ${new Date(slot.start).toISOString()} for ${card.name}. Call set_tickets for the party.`);
      },
    },
    {
      name: "set_tickets",
      description:
        "Set how many of each ticket type the party needs on the open offering — the page's own ticket steppers. Each entry names a ticket type (as list_offerings names it) and a quantity; 0 removes it.",
      inputSchema: {
        type: "object",
        properties: {
          ...PRODUCT,
          tickets: {
            type: "array",
            minItems: 1,
            items: {
              type: "object",
              properties: { ticketType: { type: "string" }, quantity: { type: "integer", minimum: 0 } },
              required: ["ticketType", "quantity"],
              additionalProperties: false,
            },
          },
        },
        required: ["productId", "tickets"],
        additionalProperties: false,
      },
      execute: async (input) => {
        const found = opened(input);
        if ("refusal" in found) return found.refusal;
        const { card, comp, groupThreshold } = found.view;
        const asked = field(input, "tickets");
        if (!Array.isArray(asked) || asked.length === 0)
          return refused(`Say which tickets — a list like [{ "ticketType": "${card.variants[0]?.name ?? "General Admission"}", "quantity": 2 }].`);
        let next: Record<string, number> = { ...comp };
        const sets: [string, number][] = [];
        for (const entry of asked) {
          const name = text(entry, "ticketType");
          const qty = field(entry, "quantity");
          const variant =
            name === undefined
              ? undefined
              : card.variants.find((v) => v.variantId === name || v.name.toLowerCase() === name.toLowerCase());
          if (variant === undefined)
            return refused(`${card.name} has no “${name ?? ""}” ticket — its tickets are ${andList(card.variants.map((v) => v.name))}.`);
          if (typeof qty !== "number" || !Number.isSafeInteger(qty) || qty < 0)
            return refused(`${variant.name}'s quantity must be a whole number, 0 or more.`);
          const cap = maxQuantity(card, variant.variantId);
          if (qty > cap) return refused(`${variant.name} sells up to ${cap} per order.`);
          next = { ...next, [variant.variantId]: qty };
          sets.push([variant.variantId, qty]);
        }
        const group = groupParty(card, next, groupThreshold);
        if (group !== undefined) return refused(groupSentence(group.size));
        for (const [variantId, qty] of sets) deps.setQuantity(variantId, qty);
        const hints = compositionIssues(card, next).map((i) => issueHint(i, card));
        const sum = partyTotal(card, next);
        return said(
          [
            `The party is ${partyLine(card, next)}${sum !== undefined ? ` — ${majorUnits(sum.amount)} ${sum.currency} before fees` : ""}.`,
            ...hints,
            hints.length === 0 ? "Call add_to_cart when the time and party are right." : "",
          ]
            .filter((l) => l !== "")
            .join(" "),
        );
      },
    },
    {
      name: "add_to_cart",
      description: "Add the open offering's party (and its time) to the cart — the tickets step's Continue. Refused with the step's own reason until it would allow it.",
      inputSchema: {
        type: "object",
        properties: {
          agent: { type: "string", description: "Your name as the agent placing this order — it is recorded on the order (channel = agent)." },
        },
        additionalProperties: false,
      },
      execute: async (input) => {
        const view = deps.tickets();
        if (view === undefined) return refused("No offering is open — call open_offering, choose_showtime and set_tickets first.");
        const { card, comp, chosenSlotId, groupThreshold } = view;
        if (view.journey) return refused(`${card.name} is a trip: its trip type and legs are picked on the page itself, not through these tools.`);
        if (totalQuantity(comp) === 0) return refused(`Pick tickets first — call set_tickets with “${card.productId}”.`);
        if (needsDepartureStep(card) && chosenSlotId === undefined)
          return refused(`Pick a time first — call choose_showtime with a slotId from list_showtimes.`);
        const hints = compositionIssues(card, comp).map((i) => issueHint(i, card));
        if (hints.length > 0) return refused(hints.join(" "));
        const group = groupParty(card, comp, groupThreshold);
        if (group !== undefined) return refused(groupSentence(group.size));
        /* B1084 (d) — the order will say an agent placed it; clipped to the
           contract's cap so a long name never refuses the buyer's checkout. */
        deps.markAgent?.((text(input, "agent") ?? AGENT_UNNAMED).slice(0, AGENT_NAME_MAX));
        deps.addToCart();
        return said(`Adding ${partyLine(card, comp)} to the cart. Call review_checkout to read the cart and its total.`);
      },
    },
    {
      name: "apply_code",
      description: "Apply a promo code to the cart — the cart's own Apply. Answers the new total, or the seller's refusal.",
      inputSchema: {
        type: "object",
        properties: { code: { type: "string", description: "The promo code as the buyer was given it." } },
        required: ["code"],
        additionalProperties: false,
      },
      execute: async (input) => {
        if (deps.checkout().lines.length === 0) return refused("The cart is empty — call add_to_cart first; a code applies to a cart.");
        const raw = field(input, "code");
        const outcome = await deps.applyCode(typeof raw === "string" ? raw : "");
        if (outcome === undefined) return refused("The cart is still answering the last change — try the code again in a moment.");
        if ("refused" in outcome) return refused(outcome.refused);
        return said(
          `The code applied.${outcome.total !== undefined ? ` The total is now ${majorUnits(outcome.total.amount)} ${outcome.total.currency}.` : ""} Call review_checkout for the cart.`,
        );
      },
    },
    {
      name: "set_buyer_details",
      description: "Fill the buyer fields the tickets are booked under: name, email, phone (each optional; send the ones to fill). Judged as the fields judge them.",
      inputSchema: {
        type: "object",
        properties: { name: { type: "string" }, email: { type: "string" }, phone: { type: "string" } },
        additionalProperties: false,
      },
      execute: async (input) => {
        const fields: { name?: string; email?: string; phone?: string } = {};
        for (const key of ["name", "email", "phone"] as const) {
          const v = field(input, key);
          if (typeof v === "string") fields[key] = v;
        }
        if (Object.keys(fields).length === 0) return refused("Send at least one of name, email or phone.");
        const problems = buyerFieldProblems({ name: fields.name ?? "", email: fields.email ?? "", phone: fields.phone ?? "" });
        const why = [
          problems.name !== undefined ? `Name: ${problems.name}` : undefined,
          problems.email !== undefined ? `Email: ${problems.email}` : undefined,
          problems.phone !== undefined ? `Phone: ${problems.phone}` : undefined,
        ].filter((w): w is string => w !== undefined);
        if (why.length > 0) return refused(why.join(" "));
        deps.setBuyer(fields);
        return said(`Filled ${andList(Object.keys(fields))}. Call review_checkout to see what is still needed.`);
      },
    },
    {
      name: "review_checkout",
      description:
        "Read the cart: its lines, discounts, fees and total, what is still needed before Pay, and how payment happens (the buyer's own card or Link in the page's payment form — no tool pays).",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      execute: async () => {
        const view = deps.checkout();
        if (view.busy) return refused("The cart is still answering the last change — ask again in a moment.");
        if (view.lines.length === 0)
          return refused(`${view.notice !== undefined ? `${view.notice} ` : ""}The cart is empty — open an offering, set tickets and call add_to_cart.`);
        const typed = buyerFieldProblems(view.buyer);
        const needed = [
          view.nameRequired && view.buyer.name.trim() === "" ? `${BUYER_NAME_MISSING} (set_buyer_details)` : undefined,
          view.emailRequired && view.buyer.email.trim() === "" ? `${BUYER_EMAIL_MISSING} (set_buyer_details)` : undefined,
          typed.name !== undefined ? `Name: ${typed.name}` : undefined,
          typed.email !== undefined ? `Email: ${typed.email}` : undefined,
          typed.phone !== undefined ? `Phone: ${typed.phone}` : undefined,
          view.acknowledgement !== undefined && !view.acknowledged
            ? `The buyer ticks “${view.acknowledgement}” on the page themselves — no tool can tick it.`
            : undefined,
        ].filter((n): n is string => n !== undefined);
        const handedOff = deps.handOff === undefined ? undefined : await deps.handOff().catch(() => undefined);
        return json({
          lines: view.lines.map((l) => ({
            offering: l.productName,
            ...(l.ticketType !== undefined ? { ticketType: l.ticketType } : {}),
            quantity: l.quantity,
            ...(l.start !== undefined ? { start: new Date(l.start).toISOString() } : {}),
            ...(l.amount !== undefined ? price(l.amount) : {}),
          })),
          ...(view.adjustments.length > 0 ? { discounts: view.adjustments.map((a) => ({ label: a.label, ...price(a.amount) })) } : {}),
          charges: view.charges.map((c) => ({ label: c.label, ...price(c.amount) })),
          ...(view.total !== undefined ? { total: price(view.total) } : {}),
          ...(view.notice !== undefined ? { notice: view.notice } : {}),
          ready: needed.length === 0,
          needed,
          payment:
            "Payment is the buyer's: the card (or Link) goes in the page's payment form and the buyer presses Pay. No tool takes a payment.",
          ...(handedOff !== undefined ? { continueUrl: handedOff.continueUrl, continueNote: CONTINUE_NOTE } : {}),
        });
      },
    },
  ];
}
